/**
 * Platinum CCTV Quote Calculator Engine - v23.1
 *
 * Keeps line-item pricing, option totals, drop quantities, and cart display math
 * consistent across all category templates.
 */

export const QUOTE_CALC_VERSION = "v23.1";

export const DEFAULT_STANDARD_FIELDS = new Set([
  "id", "sku", "option", "num_options", "category", "category_name",
  "name", "Name", "Product_Name", "Product_Description",
  "Product_Quantity", "quantity", "Product_Base_Price", "Product_FinalPrice",
  "Product_Final_Price", "unitprice", "unitPrice", "unit_price", "price", "price_2",
  "pricePerItem", "totalPriceForItem", "basePrice", "typedValue",
  "uniqueID", "type", "thumbnail", "image1", "image2", "image3", "image4",
  "drop", "extra_field_1", "extra_field_2", "extra_field_3", "extra_field_4", "extra_field_5",
  "auto_service", "autoAdded", "autoLaborType", "linkedParentUniqueID", "linkedParentSku",
  "hide_1", "hide_2",
  "sorting", "description", "categories",
  "cableOptionPrice",
]);

export function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function toQuantity(value, fallback = 1) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function money(value) {
  return Math.round(toNumber(value) * 100) / 100;
}

export function formatMoney(value) {
  return money(value).toFixed(2);
}

/**
 * Supports old flattened option fields, camera option slots, and vNext selectedOptions.
 */
export function getOptionEntries(item, extraStandardFields = []) {
  if (!item || typeof item !== "object") return [];

  const standardFields = new Set([
    ...DEFAULT_STANDARD_FIELDS,
    ...(extraStandardFields || []),
  ]);

  const entries = [];

  if (item.selectedOptions && typeof item.selectedOptions === "object") {
    Object.entries(item.selectedOptions).forEach(([name, option]) => {
      if (!name || !option) return;
      entries.push({
        name,
        value: option.value ?? option.label ?? option.name ?? "",
        price: toNumber(option.price),
      });
    });
  }

  for (let i = 1; i <= 12; i += 1) {
    const name = item[`option${i}_name`];
    const value = item[`option${i}_value`];
    const price = item[`option${i}_price`];

    if (name && value) {
      entries.push({ name, value, price: toNumber(price) });
    }
  }

  Object.keys(item).forEach((key) => {
    if (standardFields.has(key)) return;
    if (key.endsWith("_price")) return;
    if (/^option\d+_(name|value|price)$/.test(key)) return;

    const value = item[key];
    if (value === undefined || value === null || value === "") return;

    entries.push({
      name: key,
      value,
      price: toNumber(item[`${key}_price`]),
    });
  });

  const seen = new Set();
  return entries.filter((entry) => {
    const signature = `${entry.name}::${entry.value}`;
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

export function getOptionUnitTotal(item, extraStandardFields = []) {
  return getOptionEntries(item, extraStandardFields).reduce(
    (sum, option) => sum + toNumber(option.price),
    0
  );
}

export function getUnitPrice(item) {
  if (!item) return 0;

  if (item.pricePerItem !== undefined) return toNumber(item.pricePerItem);
  if (item.unitprice !== undefined) return toNumber(item.unitprice);
  if (item.unit_price !== undefined) return toNumber(item.unit_price);
  if (item.Product_FinalPrice !== undefined) return toNumber(item.Product_FinalPrice);
  if (item.Product_Base_Price !== undefined) return toNumber(item.Product_Base_Price);
  if (item.price !== undefined) return toNumber(item.price);
  if (item.price_2 !== undefined) return toNumber(item.price_2);

  return 0;
}

export function getQuantity(item) {
  if (!item) return 1;

  if (item.quantity !== undefined) return toQuantity(item.quantity);
  if (item.Product_Quantity !== undefined) return toQuantity(item.Product_Quantity);

  return 1;
}

/**
 * Canonical line calculation.
 *
 * Line total = (unit price + option unit total) * quantity.
 */
export function calculateLineItem(item, options = {}) {
  const quantity = toQuantity(options.quantity ?? getQuantity(item));
  const unitPrice = money(options.unitPrice ?? getUnitPrice(item));
  const optionUnitTotal = money(
    options.optionUnitTotal ?? getOptionUnitTotal(item, options.extraStandardFields)
  );

  const optionTotal = money(optionUnitTotal * quantity);
  const unitTotal = money(unitPrice + optionUnitTotal);
  const lineTotal = money(unitTotal * quantity);

  return {
    quantity,
    unitPrice,
    optionUnitTotal,
    optionTotal,
    unitTotal,
    lineTotal,
  };
}

export function mergeOptionPriceFields(optionValues, optionPriceMap) {
  const result = { ...(optionValues || {}) };

  Object.keys(optionPriceMap || {}).forEach((name) => {
    result[`${name}_price`] = toNumber(optionPriceMap[name]);
  });

  return result;
}


export function getProductSku(item) {
  if (!item || typeof item !== "object") return "";

  return String(
    item.id ||
      item.sku ||
      item.Product_Name ||
      item.Port_Name ||
      item.NVR_Name ||
      item.Product_SKU ||
      ""
  );
}

export function getProductDisplayName(item) {
  if (!item || typeof item !== "object") return "Item";

  const sku = getProductSku(item);
  const candidates = [
    item.Name,
    item.name,
    item.Product_Description,
    item.description,
    item.Product_Name,
  ];

  const selected = candidates.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );

  if (!selected) return sku || "Item";

  let displayName = String(selected).trim();

  // Keep the SKU in the SKU column only when descriptions repeat it.
  if (sku) {
    if (displayName === sku) return sku;

    [" - ", ": ", " "].some((separator) => {
      const prefix = sku + separator;
      if (displayName.toLowerCase().startsWith(prefix.toLowerCase())) {
        displayName = displayName.slice(prefix.length).trim();
        return true;
      }
      return false;
    });
  }

  return displayName || sku || "Item";
}

/**
 * Drop calculation rule:
 * - Every true camera counts as one drop by default.
 * - If a camera has drop/extra_field_2, that value replaces the default 1 drop
 *   for that camera line, so a qty-1 camera with extra_field_2 = 2 adds 2 drops.
 * - Non-camera items with drop/extra_field_2 remain additive drops.
 *
 * This prevents a double-drop camera from replacing the previously calculated
 * camera drops for the option.
 */
export function calculateDrops(items, fallbackCameraQuantity = 0) {
  let cameraDrops = 0;
  let additionalDrops = 0;
  let sawCameraItem = false;

  (items || []).forEach((item) => {
    const qty = getQuantity(item);
    const dropValue =
      item?.drop !== undefined && item?.drop !== ""
        ? item.drop
        : item?.extra_field_2;

    const isTrueCameraItem =
      String(item?.category || "") === "Camera" && !item?.Camera_Category_Name;

    if (isTrueCameraItem) {
      sawCameraItem = true;
      const dropsPerCamera =
        dropValue !== undefined && dropValue !== "" ? toNumber(dropValue) : 1;
      cameraDrops += dropsPerCamera * qty;
      return;
    }

    if (dropValue === undefined || dropValue === "") return;
    additionalDrops += toNumber(dropValue) * qty;
  });

  const baseCameraDrops = sawCameraItem
    ? cameraDrops
    : toNumber(fallbackCameraQuantity);

  return baseCameraDrops + additionalDrops;
}
